import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  TrendingUp, 
  Assessment, 
  // AccountBalance, 
  Speed, 
  // Security, 
  Analytics 
} from '@mui/icons-material';
import { motion } from 'framer-motion';
const Home = () => {
  const user = useSelector((state) => state.account.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const features = [
    /*{
      icon: <TrendingUp />,
      title: 'Real-time Market Data',
      description: 'Track live market movements and price changes across multiple assets',
    },*/
    {
      icon: <Assessment />,
      title: 'Trade Signal Analysis',
      description: 'AI-powered trade signals with performance tracking and analytics',
    },
    // {
    //   icon: <AccountBalance />,
    //   title: 'Portfolio Management',
    //   description: 'Comprehensive portfolio tracking with detailed performance metrics',
    // },
    {
      icon: <Speed />,
      title: 'Lightning Fast',
      description: 'Optimized for speed with real-time updates and instant responses',
    },
    /*{
      icon: <Security />,
      title: 'Secure & Reliable',
      description: 'Bank-level security with encrypted data and secure authentication',
    },*/
    {
      icon: <Analytics />,
      title: 'Advanced Analytics',
      description: 'Deep insights with charts, trends, and predictive analytics',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 8 }, pb: { xs: 6, md: 12 } }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: 'var(--dwlf-accent)',
                  lineHeight: 1.2,
                }}
              >
                Welcome to DWLF
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  mb: 4,
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                The next-generation trading platform that combines AI-powered insights 
                with real-time market data to help you make smarter investment decisions.
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 6,
                }}
              >
                {user ? (
                  <>
                    <Button
                      component={Link}
                      to="/markets"
                      variant="outlined"
                      size="large"
                      startIcon={<TrendingUp />}
                      sx={{
                        minWidth: { xs: '200px', sm: 'auto' },
                        py: 1.5,
                        px: 4,
                        borderRadius: '12px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 212, 255, 0.3)',
                        },
                      }}
                    >
                      Explore Markets
                    </Button>
                    {/* <Button
                      component={Link}
                      to="/portfolios"
                      variant="contained"
                      size="large"
                      startIcon={<AccountBalance />}
                      sx={{
                        minWidth: { xs: '200px', sm: 'auto' },
                        py: 1.5,
                        px: 4,
                        borderRadius: '12px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 212, 255, 0.4)',
                        },
                      }}
                    >
                      View Portfolios
                    </Button> */}
                  </>
                ) : (
                  <>
                    <Button
                      component={Link}
                      to="/login"
                      variant="contained"
                      size="large"
                      sx={{
                        minWidth: { xs: '200px', sm: 'auto' },
                        py: 1.5,
                        px: 4,
                        borderRadius: '12px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 212, 255, 0.4)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                    <Button
                      component={Link}
                      to="/markets"
                      variant="text"
                      size="large"
                      startIcon={<TrendingUp />}
                      sx={{
                        minWidth: { xs: '200px', sm: 'auto' },
                        py: 1.5,
                        px: 4,
                        borderRadius: '12px',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'background.surface',
                        },
                      }}
                    >
                      Explore Markets
                    </Button>
                  </>
                )}
              </Box>
            </motion.div>
          </Box>

          {/* Features Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                mb: 6,
                color: 'text.primary',
              }}
            >
              Why Choose DWLF?
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <motion.div
                  variants={itemVariants}
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.2 },
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      p: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(0, 212, 255, 0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: '16px',
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {feature.icon}
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: 'text.primary',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA Section */}
          {!user && (
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  textAlign: 'center',
                  mt: 10,
                  p: 6,
                  borderRadius: '24px',
                  backgroundColor: 'var(--dwlf-accent-10)',
                  border: '1px solid',
                  borderColor: 'primary.main',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                  }}
                >
                  Ready to Get Started?
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                    fontWeight: 400,
                  }}
                >
                  Join thousands of investors who trust DWLF for their portfolio management needs.
                </Typography>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  sx={{
                    py: 2,
                    px: 6,
                    borderRadius: '12px',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0, 212, 255, 0.4)',
                    },
                  }}
                >
                  Sign In with Google
                </Button>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default Home;